import { PASSWORT_MINDESTLAENGE } from '@nac2s/shared'
import { type FormEvent, type ReactNode, useEffect, useState } from 'react'
import { Button } from '@/components/base/buttons/button'
import { Input } from '@/components/base/input/input'
import { Logo } from '@/teile/Logo'
import { Fehler } from '@/teile/Tabelle'
import { ApiError } from '../api/client.js'
import {
  anmelden,
  einrichtungCode,
  einrichtungKonto,
  einrichtungWarten,
  ladeEinrichtung,
} from '../api/partner.js'
import { useSitzung } from '../session/SitzungContext.js'

/**
 * Einrichtungsassistent (ADR-0020).
 *
 * Ein frischer Stack hat kein Partnerkonto. Statt der Anmeldung steht hier
 * der Weg hinein: Kopplungscode von netable hinterlegen, warten, bis die
 * Verwaltung damit gekoppelt hat, dann das erste Verwalterkonto. Danach ist
 * der Partner angemeldet, und der Assistent kommt nie wieder.
 *
 * Vor der Kopplung zeigt die Seite nichts über den Stack — keinen Namen,
 * keine Fassung. Wer keinen Code hat, sieht ein Feld und sonst nichts.
 */

const WARTE_TAKT_MS = 3_000

type Schritt =
  | { name: 'code' }
  | { name: 'warten'; token: string; codeBis: string }
  | { name: 'konto'; partnerName: string; fingerabdruck: string; token: string }
  | { name: 'fertig'; partnerName: string }

export function Einrichtung({ sonst }: { sonst: ReactNode }) {
  const { neuLaden } = useSitzung()
  const [zustand, setZustand] = useState<'laedt' | 'eingerichtet' | 'assistent'>('laedt')
  const [schritt, setSchritt] = useState<Schritt>({ name: 'code' })
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)

  useEffect(() => {
    let aktiv = true
    ladeEinrichtung()
      .then((z) => {
        if (aktiv) setZustand(z.eingerichtet ? 'eingerichtet' : 'assistent')
      })
      .catch(() => {
        // Antwortet der Dienst nicht, bleibt die normale Anmeldung — sie
        // zeigt ihre eigene Fehlermeldung.
        if (aktiv) setZustand('eingerichtet')
      })
    return () => {
      aktiv = false
    }
  }, [])

  // Warten auf die Verwaltung: im Takt nachfragen, bis gekoppelt.
  useEffect(() => {
    if (schritt.name !== 'warten') return
    const { token } = schritt
    let aktiv = true
    const frage = async () => {
      try {
        const antwort = await einrichtungWarten(token)
        if (!aktiv) return
        if (antwort.gekoppelt && antwort.partnerName && antwort.fingerabdruck) {
          setSchritt({
            name: 'konto',
            partnerName: antwort.partnerName,
            fingerabdruck: antwort.fingerabdruck,
            token,
          })
        }
      } catch (fehler) {
        if (!aktiv) return
        if (fehler instanceof ApiError && fehler.status === 401) {
          setMeldung('Der Code ist verfallen — bitte einen neuen Code eingeben.')
          setSchritt({ name: 'code' })
        }
      }
    }
    void frage()
    const uhr = setInterval(() => void frage(), WARTE_TAKT_MS)
    return () => {
      aktiv = false
      clearInterval(uhr)
    }
  }, [schritt])

  if (zustand === 'laedt') {
    return (
      <p role="status" className="p-8 text-sm text-tertiary">
        Wird geladen …
      </p>
    )
  }
  if (zustand === 'eingerichtet') return <>{sonst}</>

  async function codeHinterlegen(ereignis: FormEvent<HTMLFormElement>) {
    ereignis.preventDefault()
    const code = String(new FormData(ereignis.currentTarget).get('code') ?? '')
    setLaeuft(true)
    setMeldung(null)
    try {
      const antwort = await einrichtungCode(code)
      setSchritt({ name: 'warten', token: antwort.einrichtungstoken, codeBis: antwort.codeBis })
    } catch {
      setMeldung('Der Code wurde nicht angenommen.')
    } finally {
      setLaeuft(false)
    }
  }

  async function kontoAnlegen(ereignis: FormEvent<HTMLFormElement>) {
    ereignis.preventDefault()
    if (schritt.name !== 'konto') return
    const formular = new FormData(ereignis.currentTarget)
    const email = String(formular.get('email') ?? '')
    const anzeigename = String(formular.get('anzeigename') ?? '')
    const passwort = String(formular.get('passwort') ?? '')
    if (passwort !== String(formular.get('wiederholung') ?? '')) {
      setMeldung('Die Passwörter stimmen nicht überein.')
      return
    }
    setLaeuft(true)
    setMeldung(null)
    try {
      await einrichtungKonto({ einrichtungstoken: schritt.token, email, anzeigename, passwort })
      await anmelden(email, passwort)
      setSchritt({ name: 'fertig', partnerName: schritt.partnerName })
      await neuLaden()
    } catch (fehler) {
      setMeldung(
        fehler instanceof ApiError && fehler.code === 'email_belegt'
          ? 'Diese E-Mail-Adresse ist schon vergeben.'
          : fehler instanceof ApiError && fehler.status === 401
            ? 'Die Einrichtung ist abgelaufen — bitte den Code noch einmal eingeben.'
            : 'Das Konto wurde nicht angelegt.',
      )
      if (fehler instanceof ApiError && fehler.status === 401) setSchritt({ name: 'code' })
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center p-8">
      <div className="relative flex flex-col gap-7">
        <Logo className="absolute bottom-full left-1/2 mb-8 w-[23.5rem] max-w-none -translate-x-1/2 [@media(min-height:880px)]:w-[30.5rem]" />

        {schritt.name === 'code' ? (
          <form onSubmit={codeHinterlegen} className="flex flex-col gap-4">
            <h1 className="font-display text-[22px] leading-[1.1] font-extrabold tracking-[-0.02em] text-primary">
              Einrichtung
            </h1>
            <Input
              isRequired
              name="code"
              label="Kopplungscode von netable"
              autoComplete="off"
              size="md"
              placeholder="XXXX-XXXX-XXXX"
            />
            {meldung ? <Fehler>{meldung}</Fehler> : null}
            <Button type="submit" size="md" color="primary" isDisabled={laeuft}>
              {laeuft ? 'Wird hinterlegt …' : 'Weiter'}
            </Button>
          </form>
        ) : schritt.name === 'warten' ? (
          <div className="flex flex-col gap-4">
            <h1 className="font-display text-[22px] leading-[1.1] font-extrabold tracking-[-0.02em] text-primary">
              Einrichtung
            </h1>
            <p role="status" className="text-sm text-tertiary">
              Code hinterlegt — netable koppelt diesen Server. Gültig bis{' '}
              {new Date(schritt.codeBis).toLocaleString('de-DE')}.
            </p>
            <Button
              type="button"
              size="md"
              color="secondary"
              onClick={() => setSchritt({ name: 'code' })}
            >
              Anderen Code eingeben
            </Button>
          </div>
        ) : schritt.name === 'konto' ? (
          <form onSubmit={kontoAnlegen} className="flex flex-col gap-4">
            <div>
              <h1 className="font-display text-[22px] leading-[1.1] font-extrabold tracking-[-0.02em] text-primary">
                {schritt.partnerName}
              </h1>
              <p className="mt-1 text-sm text-tertiary">Gekoppelt. Fingerabdruck des Servers:</p>
              <p className="mt-1 font-mono text-[12px] break-all text-tertiary">
                {schritt.fingerabdruck}
              </p>
            </div>
            <h2 className="text-[13px] font-semibold text-primary">Erstes Verwalterkonto</h2>
            <Input isRequired name="anzeigename" label="Name" autoComplete="name" size="md" />
            <Input
              isRequired
              name="email"
              type="email"
              label="E-Mail"
              autoComplete="username"
              size="md"
            />
            <Input
              isRequired
              name="passwort"
              type="password"
              label={`Passwort (mindestens ${PASSWORT_MINDESTLAENGE} Zeichen)`}
              autoComplete="new-password"
              size="md"
            />
            <Input
              isRequired
              name="wiederholung"
              type="password"
              label="Passwort wiederholen"
              autoComplete="new-password"
              size="md"
            />
            {meldung ? <Fehler>{meldung}</Fehler> : null}
            <Button type="submit" size="md" color="primary" isDisabled={laeuft}>
              {laeuft ? 'Wird angelegt …' : 'Konto anlegen und anmelden'}
            </Button>
          </form>
        ) : (
          <p role="status" className="text-sm text-tertiary">
            Eingerichtet — Sie werden angemeldet …
          </p>
        )}
      </div>
    </main>
  )
}
